import {forwardRef} from 'react';
import useTranslation from 'next-translate/useTranslation';
import {twMerge} from 'tailwind-merge';

import InfoBanner from '@/components/global/layout/AppBar/EventBarV2/InfoBanner';
import UseCurrentLocationRow from '@/components/global/layout/AppBar/EventBarV2/Where/CurrentLocationRow';
import {
  type AddressSearchAutocompleteProps,
  type AddressSearchAutocompleteSuffixProps,
} from '../types';
import useAddressGeolocation from '../useAddressGeolocation';

export type AddressSearchAutocompleteExperimentEmptyListboxState = Pick<
  AddressSearchAutocompleteSuffixProps,
  'geocodedAddressResultHelpers' | 'inputHelpers'
> & {
  currentLocationOptionId: string;
  isCurrentLocationActive?: boolean;
  onAddressFilled?: () => void;
  variation: NonNullable<AddressSearchAutocompleteProps['experimentVariation']>;
};

type AddressSearchAutocompleteExperimentEmptyListboxProps = Omit<
  React.ComponentPropsWithoutRef<'ul'>,
  'children' | 'onMouseDown'
> &
  AddressSearchAutocompleteExperimentEmptyListboxState & {
    onMouseDown?: React.MouseEventHandler<HTMLElement>;
  };

const bannerVariant = {
  treatment_text_prompt: 'info',
  treatment_red_dot: 'warning',
} as const;

const AddressSearchAutocompleteExperimentEmptyListbox = forwardRef<
  HTMLUListElement,
  AddressSearchAutocompleteExperimentEmptyListboxProps
>(function AddressSearchAutocompleteExperimentEmptyListbox(
  {
    className,
    currentLocationOptionId,
    geocodedAddressResultHelpers,
    inputHelpers,
    isCurrentLocationActive = false,
    onAddressFilled,
    onMouseDown,
    style,
    variation,
    ...listboxProps
  },
  ref,
) {
  const {t} = useTranslation('common');
  const {t: tAppBar} = useTranslation('app-bar');
  const {getGeolocation, hasErrors, isLoading} = useAddressGeolocation({
    geocodedAddressResultHelpers,
    inputHelpers,
    onAddressFilled,
  });

  return (
    <div
      className={twMerge('flex flex-col gap-6 p-6', className)}
      onMouseDown={onMouseDown}
      role="presentation"
      style={style}
    >
      <ul
        aria-label={t('components.AddressSearchAutocomplete.aria.label')}
        role="listbox"
        {...listboxProps}
        ref={ref}
      >
        <li role="none">
          <UseCurrentLocationRow
            id={currentLocationOptionId}
            isActive={isCurrentLocationActive}
            isLoading={isLoading}
            onClick={getGeolocation}
          />
        </li>
      </ul>

      {hasErrors ? (
        <InfoBanner
          data-testid="address-search-current-location-error"
          role="alert"
          text={tAppBar('eventBarV2.where.useCurrentLocation.error')}
          variant={bannerVariant[variation]}
        />
      ) : (
        <InfoBanner
          data-testid="address-search-precise-address-nudge"
          text={t('components.AddressSearchAutocomplete.preciseAddressNudge')}
          variant={bannerVariant[variation]}
        />
      )}
    </div>
  );
});

export default AddressSearchAutocompleteExperimentEmptyListbox;
